import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const from = location.state?.from?.pathname || "/client";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Erro ao fazer login");

      login(data.user, data.access_token);
      toast.success("Login realizado com sucesso!");
      navigate(data.user.role === "provider" ? "/provider" : from, { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/client";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen-mobile bg-white flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="px-5 py-4">
        <button 
          onClick={() => navigate("/")}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors touch-target"
        >
          <ArrowLeft className="w-5 h-5 text-neutral-700" />
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center px-5 pb-10">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <Link to="/" className="text-xl font-semibold tracking-tight mb-10 block" data-testid="logo-link">
            eZelo.
          </Link>

          {/* Title */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-neutral-900">
              Bem-vindo de volta
            </h1>
            <p className="text-neutral-500 mt-1">Entre na sua conta</p>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 font-medium border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 rounded-xl mb-6"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar com Google
          </Button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-white text-sm text-neutral-500">ou</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Email</label>
              <input
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-minimal"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-sm font-medium text-neutral-700">Senha</label>
                <button type="button" className="text-sm text-neutral-500 hover:text-neutral-700">
                  Esqueceu?
                </button>
              </div>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-minimal"
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-minimal btn-primary rounded-xl mt-6"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </Button>
          </form>

          {/* Register Link */}
          <p className="text-center mt-8 text-neutral-500 text-sm">
            Não tem conta?{" "}
            <Link to="/register" className="text-neutral-900 font-medium hover:underline">
              Criar conta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
